export type BasePlacement = 'top' | 'bottom' | 'left' | 'right';
export type PlacementAlignment = -1 | 0 | 1;
export type Placement =
  | 'top'
  | 'topleft'
  | 'topright'
  | 'bottom'
  | 'bottomleft'
  | 'bottomright'
  | 'left'
  | 'lefttop'
  | 'leftbottom'
  | 'right'
  | 'righttop'
  | 'rightbottom';
export type Trigger = 'click' | 'hover' | 'focus' | 'manual';
export type DelayConfig = number | { show?: number; hide?: number };

export const DEFAULT_HIDE_DELAY_MS = 50;
export const DEFAULT_SHOW_DELAY_MS = 50;

/**
 * Fallback when `--p-popover-gutter` is missing. Matches PrimeVue's popover
 * token `gutter: '10px'` so first-paint / unthemed layout stays aligned.
 */
export const DEFAULT_POPOVER_GUTTER_PX = 10;

export const VIEWPORT_EDGE_INSET_PX = 8;

// Top/bottom placements: horizontal arrow offset via PrimeVue's `--p-popover-arrow-left` token.
export const PRIMEVUE_POPOVER_ARROW_LEFT_CSS_VAR = '--p-popover-arrow-left';
// Left/right placements: vertical arrow offset via our custom way (PrimeVue has no side-arrow support).
export const STANDARD_POPOVER_ARROW_TOP_CSS_VAR = '--standard-popover-arrow-top';

// Placement map keyed by the full compound name.
// First word = base side (where the popover appears relative to the target).
// Second word = cross-axis direction the popover leans toward:
//   -1 → leans toward START (left / top)
//   +1 → leans toward END   (right / bottom)
//
// computeAlignedPosition centers the popover on the target's start/end edge for -1/+1,
// which makes the shift always visible regardless of relative target/popover sizes.
const PLACEMENT_CONFIG: Record<string, { base: BasePlacement; alignment: PlacementAlignment }> = {
  top: { base: 'top', alignment: 0 },
  topleft: { base: 'top', alignment: -1 },
  topright: { base: 'top', alignment: 1 },
  bottom: { base: 'bottom', alignment: 0 },
  bottomleft: { base: 'bottom', alignment: -1 },
  bottomright: { base: 'bottom', alignment: 1 },
  left: { base: 'left', alignment: 0 },
  lefttop: { base: 'left', alignment: -1 },
  leftbottom: { base: 'left', alignment: 1 },
  right: { base: 'right', alignment: 0 },
  righttop: { base: 'right', alignment: -1 },
  rightbottom: { base: 'right', alignment: 1 },
};

export const parsePlacement = (placement: string): { base: BasePlacement; alignment: PlacementAlignment } => {
  const normalized = placement.toLowerCase().replace(/-/g, '');

  return PLACEMENT_CONFIG[normalized] ?? PLACEMENT_CONFIG.right;
};

export const clamp = (value: number, min: number, max: number): number => {
  return Math.max(min, Math.min(value, max));
};

// Centers the popover on the target's start edge (-1), center (0), or end edge (+1).
// Using the target's edge as the center point (rather than edge-to-edge anchoring) ensures
// the shift is always visually distinct regardless of how wide the popover is relative to the target.
export const computeAlignedPosition = (
  alignment: PlacementAlignment,
  targetStart: number,
  targetSize: number,
  popoverSize: number,
  scrollOffset: number,
  min: number,
  max: number,
): number => {
  let anchor: number;

  if (alignment === -1) {
    anchor = targetStart;
  } else if (alignment === 1) {
    anchor = targetStart + targetSize;
  } else {
    anchor = targetStart + targetSize / 2;
  }

  return clamp(anchor - popoverSize / 2 + scrollOffset, min, max);
};

/**
 * Nudges the popover along the horizontal axis so the top/bottom arrow can reach the target
 * center without hitting the popover edge, then re-clamps to the viewport.
 */
export const shiftPopoverHorizontalForArrow = (
  popoverLeftViewport: number,
  targetCenterX: number,
  popoverWidth: number,
  arrowInsetPx: number,
  minLeftViewport: number,
  maxLeftViewport: number,
): number => {
  const arrowMin = arrowInsetPx;
  const arrowMax = popoverWidth - arrowInsetPx;
  const idealArrowLeft = targetCenterX - popoverLeftViewport;

  if (idealArrowLeft > arrowMax) {
    popoverLeftViewport -= idealArrowLeft - arrowMax;
  } else if (idealArrowLeft < arrowMin) {
    popoverLeftViewport += arrowMin - idealArrowLeft;
  }

  return clamp(popoverLeftViewport, minLeftViewport, maxLeftViewport);
};

/**
 * Nudges the popover along the vertical axis so the left/right arrow can reach the target
 * center without hitting the popover edge, then re-clamps to the viewport.
 */
export const shiftPopoverVerticalForArrow = (
  popoverTopViewport: number,
  targetCenterY: number,
  popoverHeight: number,
  arrowInsetPx: number,
  minTopViewport: number,
  maxTopViewport: number,
): number => {
  const arrowMin = arrowInsetPx;
  const arrowMax = popoverHeight - arrowInsetPx;
  const idealArrowTop = targetCenterY - popoverTopViewport;

  if (idealArrowTop > arrowMax) {
    popoverTopViewport -= idealArrowTop - arrowMax;
  } else if (idealArrowTop < arrowMin) {
    popoverTopViewport += arrowMin - idealArrowTop;
  }

  return clamp(popoverTopViewport, minTopViewport, maxTopViewport);
};

export const resolveTarget = (target: string | undefined): HTMLElement | null => {
  if (!target) {
    return null;
  }

  return document.getElementById(target.replace(/^#/, ''));
};

export const parseTriggers = (triggers: Trigger | Trigger[] | string | undefined): Trigger[] => {
  if (Array.isArray(triggers)) {
    return triggers;
  }

  if (typeof triggers === 'string') {
    return triggers.split(/\s+/).filter(Boolean) as Trigger[];
  }

  return ['click'];
};

export const parseDelay = (delay: DelayConfig | undefined): { show: number; hide: number } => {
  if (typeof delay === 'number') {
    return { show: delay, hide: delay };
  }

  if (delay && typeof delay === 'object') {
    return {
      show: delay.show ?? DEFAULT_SHOW_DELAY_MS,
      hide: delay.hide ?? DEFAULT_HIDE_DELAY_MS,
    };
  }

  return { show: DEFAULT_SHOW_DELAY_MS, hide: DEFAULT_HIDE_DELAY_MS };
};

/** Midpoint of the target box. Nested SVG/icons are ignored so table cells stay row-centered. */
export const getTargetBoxCenter = (target: HTMLElement): { x: number; y: number } => {
  const rect = target.getBoundingClientRect();

  return {
    x: rect.left + rect.width / 2,
    y: rect.top + rect.height / 2,
  };
};

export const resolvePopoverGutterPx = (popover: HTMLElement): number => {
  const gutterValue = parseFloat(getComputedStyle(popover).getPropertyValue('--p-popover-gutter'));

  return Number.isFinite(gutterValue) ? gutterValue : DEFAULT_POPOVER_GUTTER_PX;
};

/**
 * Centers the arrow on the target along one axis using PrimeVue's equal-gutter triangle
 * (`border-width: gutter` on all sides, so the visible base is `2 × gutter`).
 * - `vertical`: left/right placement; CSS `top` + `margin-top: -gutter`. Offset is from the
 *   painted overlay's getBoundingClientRect().top, not the planned popoverTopViewport.
 * - `horizontal`: top/bottom placement; CSS `left` with the same gutter triangle geometry.
 */
export const centerArrowOn = (
  popover: HTMLElement,
  axis: 'vertical' | 'horizontal',
  targetCenter: number,
  popoverSize: number,
): number => {
  const isVertical = axis === 'vertical';
  const popoverStyles = getComputedStyle(popover);
  const popoverBorder = parseFloat(popoverStyles[isVertical ? 'borderTopWidth' : 'borderLeftWidth']) || 0;
  const popoverOrigin = popover.getBoundingClientRect()[isVertical ? 'top' : 'left'];
  const gutter = resolvePopoverGutterPx(popover);
  const arrowMargin = -gutter;
  const arrowSize = gutter * 2;
  const arrowPos = targetCenter - popoverOrigin - popoverBorder - arrowMargin - arrowSize / 2;

  return clamp(arrowPos, VIEWPORT_EDGE_INSET_PX, popoverSize - arrowSize - VIEWPORT_EDGE_INSET_PX);
};

export const setPrimeVueArrowState = (popover: HTMLElement, base: BasePlacement): void => {
  const isFlipped = base === 'top';

  popover.classList.toggle('p-popover-flipped', isFlipped);

  if (isFlipped) {
    popover.setAttribute('data-p-popover-flipped', 'true');
    return;
  }

  popover.removeAttribute('data-p-popover-flipped');
};

export const setPrimeVueArrowOffset = (popover: HTMLElement, targetCenterX: number): void => {
  const arrowLeft = centerArrowOn(popover, 'horizontal', targetCenterX, popover.offsetWidth);

  popover.style.setProperty(PRIMEVUE_POPOVER_ARROW_LEFT_CSS_VAR, `${arrowLeft}px`);
  popover.style.removeProperty(STANDARD_POPOVER_ARROW_TOP_CSS_VAR);
};

export const setSideArrowOffset = (popover: HTMLElement, targetCenterY: number, viewportDeltaPx: number): void => {
  // `centerArrowOn` is viewport-local (target Y minus painted overlay top). CSS `top` on
  // the overlay is offset-parent space, so paint sits `viewportDeltaPx` below that value;
  // add it so the side-arrow tip matches `targetCenterY - style.top` without using style.top
  // (that mix breaks when the page is scrolled).
  const arrowSizePx = resolvePopoverGutterPx(popover) * 2;
  const arrowTop = clamp(
    centerArrowOn(popover, 'vertical', targetCenterY, popover.offsetHeight) + viewportDeltaPx,
    VIEWPORT_EDGE_INSET_PX,
    popover.offsetHeight - arrowSizePx - VIEWPORT_EDGE_INSET_PX,
  );

  popover.style.setProperty(STANDARD_POPOVER_ARROW_TOP_CSS_VAR, `${arrowTop}px`);
  popover.style.removeProperty(PRIMEVUE_POPOVER_ARROW_LEFT_CSS_VAR);
};
